import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  ListItem,
  Menu,
  MenuItem,
  ThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useUserContext } from "../providers/userProvider";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { theme } from "../../customization/customTheme";
import concept from "../../LCCU-concept.png";
import React from "react";

export const PrivateRoute: FC = () => {
  const { user, logout, isLoading } = useUserContext();

  const pages = ["GSM", "Overzicht", "Contact"];

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (!user && !isLoading) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate, isLoading]);

  if (isLoading) {
    return (
      <Box
        height="100vh"
        display="flex"
        flexDirection="column"
        justifyContent={"center"}
        alignContent={"center"}
        alignItems={"center"}
        alignSelf={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      height="100vh"
      display="flex"
      flexDirection="column"
      sx={{
        background:
          "linear-gradient(34deg, #c2c2e0 0%, #ebebf5 50%, #ffffff 100%);",
      }}
      min-height={"100vh"}
    >
      <ThemeProvider theme={theme}>
        <Box height="100vh" display="flex" flexDirection="column">
          <AppBar position="static">
            <Container maxWidth="xl">
              <Toolbar disableGutters>
                <Box
                  component="img"
                  alt="concept LCCU"
                  src={concept}
                  sx={{ width: 100, paddingRight: 5 }}
                  alignContent={"left"}
                ></Box>
                <Typography
                  variant="subtitle1"
                  noWrap
                  component="a"
                  href="/"
                  sx={{
                    mr: 2,
                    display: { xs: "none", md: "flex" },
                    fontFamily: "monospace",
                    fontWeight: 700,
                    letterSpacing: ".3rem",
                    color: "inherit",
                    textDecoration: "none",
                    paddingRight: 20,
                  }}
                  onClick={() => navigate("/")}
                >
                  PZ Rupel - LCCU
                </Typography>

                <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleOpenNavMenu}
                    color="inherit"
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorElNav}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                    sx={{
                      display: { xs: "block", md: "none" },
                    }}
                  >
                    {pages.map((page) => (
                      <MenuItem key={page} onClick={handleCloseNavMenu}>
                        <Typography
                          sx={{ textAlign: "center" }}
                          onClick={() => navigate(`/${page}`)}
                        >
                          {page}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>

                <Typography
                  variant="h5"
                  noWrap
                  component="a"
                  href="/"
                  sx={{
                    mr: 2,
                    display: { xs: "flex", md: "none" },
                    flexGrow: 1,
                    fontFamily: "monospace",
                    fontWeight: 500,
                    letterSpacing: ".3rem",
                    color: "inherit",
                    textDecoration: "none",
                  }}
                  onClick={() => navigate("/")}
                >
                  LCCU
                </Typography>
                <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                  {pages.map((page) => (
                    <Button
                      key={page}
                      onClick={() => {
                        navigate(`/${page}`);
                        handleCloseNavMenu();
                      }}
                      sx={{
                        my: 2,
                        color: "white",
                        display: "block",
                        marginRight: 20,
                      }}
                    >
                      {page}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Button variant="outlined" sx={{ color: "white" }}>
                        {user?.displayName}
                      </Button>
                    </IconButton>
                  </Tooltip>

                  <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem onClick={handleCloseUserMenu}>
                      <Grid
                        container
                        direction="column"
                        spacing={2}
                        flex={5}
                        marginBlock={2}
                        textAlign={"center"}
                        alignContent={"center"}
                        alignItems={"center"}
                        alignSelf={"center"}
                      >
                        <Divider component={"li"} variant="fullWidth" />
                        <ListItem
                          component="li"
                          sx={{ textAlign: "center" }}
                          alignItems="center"
                          onClick={() => navigate(`/profile/${user?.uid}`)}
                        >
                          Profile
                        </ListItem>
                        <ListItem component="li" onClick={logout}>
                          Logout
                        </ListItem>
                      </Grid>
                    </MenuItem>
                  </Menu>
                </Box>
              </Toolbar>
            </Container>
          </AppBar>
          <Box
            flexGrow={1}
            overflow="auto"
            display="flex"
            flexDirection="column"
          >
            <Outlet />
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
};
